import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

import {
	areCartItemsEqual,
	calculateTotalPrice,
	calculateTotalPriceWithoutDiscount,
	fromJSON,
	hasAttendees,
	type CourseCartItem,
} from '../models/CourseCartItem';

interface CartState {
	cartItems: CourseCartItem[];
	totalPrice: () => number;
	totalPriceWithoutDiscount: () => number;
	isEmpty: () => boolean;
	cartItemsWithAttendees: () => CourseCartItem[];
	addToCart: (cartItem: CourseCartItem) => void;
	removeFromCart: (cartItem: CourseCartItem) => void;
	updateCartItem: (cartItem: CourseCartItem) => void;
	isAlreadyInCart: (cartItem: CourseCartItem) => boolean;
	reset: () => void;
}

export const useCart = create<
	CartState,
	[['zustand/devtools', never], ['zustand/persist', CartState]]
>(
	devtools(
		persist(
			(set, get) => ({
				cartItems: [],
				totalPrice: () =>
					get().cartItems.reduce((acc, cartItem) => acc + calculateTotalPrice(cartItem), 0),
				totalPriceWithoutDiscount: () =>
					get().cartItems.reduce(
						(acc, cartItem) => acc + calculateTotalPriceWithoutDiscount(cartItem),
						0,
					),
				isEmpty: () => get().cartItems.length === 0,
				cartItemsWithAttendees: () => get().cartItems.filter(hasAttendees),
				addToCart: (cartItemToAdd) => {
					set((state) => {
						const newCartItems = [...state.cartItems, cartItemToAdd];
						window?.plausible?.('cart_item_added', {
							props: {
								course_type_slug: cartItemToAdd.typeSlug,
								course_slug: cartItemToAdd.slug,
								attendee_count: String(cartItemToAdd.attendees.length),
								new_cart_item_count: String(newCartItems.length),
								total_price: String(
									newCartItems.reduce((acc, cartItem) => acc + calculateTotalPrice(cartItem), 0),
								),
							},
						});
						return { cartItems: newCartItems };
					});
				},
				removeFromCart: (cartItemToRemove) => {
					set((state) => {
						const newCartItems = state.cartItems.filter(
							(cartItem) => !areCartItemsEqual(cartItem, cartItemToRemove),
						);
						if (newCartItems.length !== state.cartItems.length) {
							window?.plausible?.('cart_item_removed', {
								props: {
									course_type_slug: cartItemToRemove.typeSlug,
									course_slug: cartItemToRemove.slug,
									attendee_count: String(cartItemToRemove.attendees.length),
									new_cart_item_count: String(newCartItems.length),
									total_price: String(
										newCartItems.reduce((acc, cartItem) => acc + calculateTotalPrice(cartItem), 0),
									),
								},
							});
						}
						return { cartItems: newCartItems };
					});
				},
				updateCartItem: (cartItem) => {
					set((state) => ({
						cartItems: state.cartItems.map((c) => (areCartItemsEqual(c, cartItem) ? cartItem : c)),
					}));
				},
				isAlreadyInCart: (cartItem) =>
					get().cartItems.filter((c) => areCartItemsEqual(c, cartItem)).length > 0,
				reset: () => {
					set({ cartItems: [] });
				},
			}),
			{
				name: 'cart',
				deserialize: (json) => {
					try {
						if (!json) return JSON.parse(json);

						const parsed = JSON.parse(json);
						const version = parsed.version;
						const state = parsed.state;

						return {
							state: {
								...state,
								cartItems: state.cartItems.map(fromJSON),
							},
							version,
						};
					} catch (error) {
						localStorage.removeItem('cart');
						return null;
					}
				},
			},
		),
	),
);

export const Selectors = {
	cartItems: (state: CartState) => state.cartItems,
	isEmpty: (state: CartState) => state.isEmpty(),
	totalPrice: (state: CartState) => state.totalPrice(),
	totalPriceWithoutDiscount: (state: CartState) => state.totalPriceWithoutDiscount(),
	cartItemsWithAttendees: (state: CartState) => state.cartItemsWithAttendees(),
	addToCart: (state: CartState) => state.addToCart,
	removeFromCart: (state: CartState) => state.removeFromCart,
	updateCartItem: (state: CartState) => state.updateCartItem,
	isAlreadyInCart: (state: CartState) => state.isAlreadyInCart,
	reset: (state: CartState) => state.reset,
} as const;
